import { inject, Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageKeys } from '../constants/local-storage.constants';
import { UserRole } from '../enums/user-role.enum';
import { LocalStorageService } from './local-storage.service';

export interface DecodedToken {
  username: string;
  email: string;
  role: UserRole;
  nbf: number;
  exp: number;
  iat: number;
  iss: string;
  aud: string;
}

@Injectable({
  providedIn: 'root',
})
export class JwtService {
  private localStorageService = inject(LocalStorageService);

  private currentUserSubject = new BehaviorSubject<DecodedToken | null>(null);
  public currentUser$: Observable<DecodedToken | null> =
    this.currentUserSubject.asObservable();

  private userRoleSubject = new BehaviorSubject<UserRole | null>(null);
  public userRole$: Observable<UserRole | null> =
    this.userRoleSubject.asObservable();

  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  public isLoggedIn$: Observable<boolean> =
    this.isLoggedInSubject.asObservable();

  constructor() {
    this.initializeUserState();
  }

  getDecodedToken(token: string): DecodedToken | null {
    try {
      return jwtDecode<DecodedToken>(token);
    } catch (error) {
      console.error('Invalid JWT Token', error);
      return null;
    }
  }

  getToken(): string | null {
    return this.localStorageService.getItemWithExpiry(
      LocalStorageKeys.ACCESS_TOKEN
    );
  }

  getCurrentUserFromToken(): DecodedToken | null {
    const token = this.getToken();
    return token ? this.getDecodedToken(token) : null;
  }

  getCurrentUserSync(): DecodedToken | null {
    return this.currentUserSubject.value;
  }

  getCurrentUserRole(): UserRole | null {
    return this.userRoleSubject.value;
  }

  isUserLoggedIn(): boolean {
    return this.isLoggedInSubject.value;
  }

  updateToken(token: string | null): void {
    if (token) {
      this.localStorageService.setItemWithExpiry(
        LocalStorageKeys.ACCESS_TOKEN,
        token
      );
    } else {
      this.localStorageService.removeItem(LocalStorageKeys.ACCESS_TOKEN);
    }
    this.updateUserState();
  }

  private initializeUserState(): void {
    this.updateUserState();
  }

  private updateUserState(): void {
    const user = this.getCurrentUserFromToken();
    this.currentUserSubject.next(user);
    this.userRoleSubject.next(user?.role ?? null);
    this.isLoggedInSubject.next(!!user);
  }
}
